import "./global.css";
// Brand-preview overrides (BRAND_TOGGLE_PLAN) — inert unless <html> carries
// data-brand="brandb". The generated sheet is produced by
// scripts/generate-brand-overrides.mjs (npm run brand:overrides).
import "./styles/brandb.css";
import "./styles/brandb-overrides.generated.css";

import React, { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { ThemeProvider } from "@/lib/theme-provider";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { OnboardingProvider } from "@/context/OnboardingContext";
import { Loader2 } from "lucide-react";
import { useTraceOnRouteChange } from "@/lib/useTraceOnRouteChange";

/**
 * Lives inside BrowserRouter so it can call useLocation(); mints a fresh
 * W3C trace-id on every route navigation. Renders nothing.
 */
function TraceBoundary(): null {
  useTraceOnRouteChange();
  return null;
}

/**
 * Route-scoped error boundary. Keyed to the pathname so browser
 * back/forward (or any navigation) RESETS a caught error instead of
 * trapping the user on the error screen until a hard refresh — the render
 * crash that surfaced this lived on the Diagnose run page's Cost sub-tab
 * (2026-07-20). The root ErrorBoundary outside the Router remains as the
 * last-resort net for crashes above the route tree.
 */
function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}

// Lazy-load every route page — Login was previously eager, but a Suspense
// boundary wrap on the auth route is cheap and saves ~28 KB on the entry
// chunk for any user who isn't currently sitting on `/login` (i.e. nearly
// all returning users, who hit a deep link or `/rune` post-redirect).
const Login = lazy(() => import("./pages/Login"));
const OnboardingContainer = lazy(() =>
  import("./pages/onboarding/OnboardingContainer").then((m) => ({
    default: m.OnboardingContainer,
  })),
);
const LegacyDepartmentRedirect = lazy(
  () => import("./components/ignite-forms/LegacyDepartmentRedirect"),
);
const LegacyRoleRedirect = lazy(
  () => import("./components/ignite-forms/LegacyRoleRedirect"),
);
const PublicRunViewPage = lazy(() => import("./pages/PublicRunView"));
const PublicBriefViewPage = lazy(() => import("./pages/PublicBriefView"));
const PublicFindingsViewPage = lazy(() => import("./pages/PublicFindingsView"));
const StakeholderInterviewPage = lazy(
  () => import("./pages/public/StakeholderInterviewPage"),
);
const RoleInvitePage = lazy(() => import("./pages/public/RoleInvitePage"));
const TaskInterviewPage = lazy(
  () => import("./pages/public/TaskInterviewPage"),
);
const TeamCheckInPage = lazy(() => import("./pages/public/TeamCheckInPage"));
const HelpPage = lazy(() => import("./pages/Help"));
const ProjectInterview = lazy(() => import("./pages/ProjectInterview"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ClaimOrganization = lazy(() => import("./pages/ClaimOrganization"));
const AcceptInvitation = lazy(() => import("./pages/AcceptInvitation"));
const SendOrganizationClaim = lazy(
  () => import("./pages/SendOrganizationClaim"),
);
const TrialManagement = lazy(() => import("./pages/admin/TrialManagement"));
const TrialExpired = lazy(() => import("./pages/TrialExpired"));
const InterviewReviewDetail = lazy(
  () => import("./pages/InterviewReviewDetail"),
);
const EmployeeInterview = lazy(() => import("./pages/EmployeeInterview"));
const EmployeeTaskReview = lazy(() => import("./pages/EmployeeTaskReview"));




// Admin pages
const AdminPromptOptimization = lazy(
  () => import("./pages/AdminPromptOptimization"),
);

// RUNE pages + global overlays
const PrototypePage = lazy(() => import("./pages/task/PrototypePage"));
const InterviewIndexPage = lazy(
  () => import("./pages/task/InterviewIndexPage"),
);
const DiagnosePage = lazy(() => import("./pages/task/DiagnosePage"));
const DiagnoseCompaniesPage = lazy(
  () => import("./pages/task/DiagnoseCompaniesPage"),
);
const CompanyDetailPage = lazy(() => import("./pages/task/CompanyDetailPage"));
const InterviewsPage = lazy(() => import("./pages/task/InterviewsPage"));
const TemplatesCatalogPage = lazy(
  () => import("./pages/task/TemplatesCatalogPage"),
);
const TemplateStartPage = lazy(() => import("./pages/task/TemplateStartPage"));
const ProjectContextPage = lazy(
  () => import("./pages/task/ProjectContextPage"),
);
const ProjectSessionPage = lazy(
  () => import("./pages/task/ProjectSessionPage"),
);
const RoleBatchInvitePage = lazy(
  () => import("./pages/task/RoleBatchInvitePage"),
);
const RoleBatchProgressPage = lazy(
  () => import("./pages/task/RoleBatchProgressPage"),
);
const RoleSpdPage = lazy(() => import("./pages/task/RoleSpdPage"));
const RoleConsolidationPage = lazy(
  () => import("./pages/task/RoleConsolidationPage"),
);
const ProspectRunPage = lazy(() => import("./pages/task/ProspectRunPage"));
const InterviewModeReviewPage = lazy(
  () => import("./pages/task/InterviewModeReviewPage"),
);
const InterviewTypeAuthorWizard = lazy(
  () => import("./pages/task/InterviewTypeAuthorWizard"),
);
const InterviewTypeSandboxRun = lazy(
  () => import("./pages/task/InterviewTypeSandboxRun"),
);
// PLAT-331 — workflow-template authoring (merged Interviews tab).
const ProjectTemplateAuthorWizard = lazy(
  () => import("./pages/task/ProjectTemplateAuthorWizard"),
);
const ProjectTemplateSandboxRun = lazy(
  () => import("./pages/task/ProjectTemplateSandboxRun"),
);
const SettingsPage = lazy(() => import("./pages/task/SettingsPage"));
const OrgPage = lazy(() => import("./pages/task/OrgPage"));
const OrgUnitPage = lazy(() => import("./pages/task/OrgUnitPage"));
const OrgRolePage = lazy(() => import("./pages/task/OrgRolePage"));

// Page loader component for Suspense fallback
const PageLoader = () => (
  <div className="flex h-screen items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

// Helper component to wrap lazy-loaded components with Suspense
interface LazyRouteProps {
  children: React.ReactNode;
}

const LazyRoute: React.FC<LazyRouteProps> = ({ children }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

/**
 * Legacy `/new/*` route redirect — preserves any path remainder, query, and
 * hash when the user lands on the old URL via bookmark or external link.
 *
 * Used for the four-tab IA rename (Frontend IA Functional Spec §7.2):
 * `/new/prospect/...` → `/new/diagnose/...`, `/new/interview/...` →
 * `/new/document/...`, `/new/spark` → `/new/design`. The wildcard route
 * `/new/<old>/*` matches both the bare path and every sub-path, and this
 * component reconstructs the equivalent path under the new prefix.
 *
 * `nestedPrefix`, when set, is inserted between `newPrefix` and a
 * non-empty remainder. Needed for prospect → diagnose because the new
 * run-detail routes live under `/new/diagnose/run/:runId`, not
 * `/new/diagnose/:runId` — a flat prefix swap would 404.
 */
export function RedirectFromOldPath({
  oldPrefix,
  newPrefix,
  nestedPrefix,
}: {
  oldPrefix: string;
  newPrefix: string;
  nestedPrefix?: string;
}) {
  const { pathname, search, hash } = useLocation();
  const remainder = pathname.startsWith(oldPrefix)
    ? pathname.slice(oldPrefix.length)
    : "";
  const hasRemainder = remainder.length > 0 && remainder !== "/";
  const target =
    hasRemainder && nestedPrefix
      ? `${newPrefix}${nestedPrefix}${remainder}`
      : `${newPrefix}${remainder}`;
  return <Navigate to={`${target}${search}${hash}`} replace />;
}

/**
 * Cutover 2026-07-20 — the legacy org pages lived at /organization/unit/
 * :unitId(/:role); the redesign's shape inserts a /role/ segment. Param-
 * aware bridge so old bookmarks keep resolving.
 */
export function LegacyOrgUnitRedirect() {
  const { unitId, role } = useParams<{ unitId: string; role?: string }>();
  return (
    <Navigate
      to={role ? `/org/unit/${unitId}/role/${role}` : `/org/unit/${unitId}`}
      replace
    />
  );
}

/**
 * Legacy /ignite/:runId(/tab) → /research/run/:runId. Tab names mostly
 * map 1:1 (pipeline); "projects" became the opportunities tab; the rest
 * (playbooks/analytics/roadmap/council/sales-prep) collapse to the run
 * overview, which links onward.
 */
export function LegacyIgniteRunRedirect() {
  const { runId } = useParams<{ runId: string }>();
  const { pathname } = useLocation();
  // Two legacy families: /ignite/:runId/* (app links) and
  // /ignite/runs/:runId/* (email + stored bell-notification links).
  const tail =
    pathname.split(`/ignite/runs/${runId}`)[1] ??
    pathname.split(`/ignite/${runId}`)[1] ??
    "";
  const tab = tail.split("/").filter(Boolean)[0];
  const suffix =
    tab === "pipeline" ? "/pipeline" : tab === "projects" ? "/opportunities" : "";
  return <Navigate to={`/research/run/${runId}${suffix}`} replace />;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider defaultTheme="light" storageKey="platform-theme">
          <QueryClientProvider client={queryClient}>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <TraceBoundary />
                <RouteErrorBoundary>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <LazyRoute>
                        <Login />
                      </LazyRoute>
                    }
                  />
                  <Route
                    path="/login"
                    element={
                      <LazyRoute>
                        <Login />
                      </LazyRoute>
                    }
                  />

                  {/* The LITE branded rep shell (/openai, /rep, /rep/run/:id)
                    was DELETED 2026-08-26: rep intake moved to the Google
                    Form → Accelerate automation and reps get the one-pager
                    by email. Run data lives on; internal viewing happens
                    through the normal research run pages. */}

                  {/* Public run share — no auth. Prospects open these links
                    in their browser without creating an account. */}
                  <Route
                    path="/public/run/:token"
                    element={
                      <LazyRoute>
                        <PublicRunViewPage />
                      </LazyRoute>
                    }
                  />

                  {/* Public decision-brief share — same trust model as the
                    run share: unguessable token, read-only, no account. */}
                  <Route
                    path="/public/brief/:token"
                    element={
                      <LazyRoute>
                        <PublicBriefViewPage />
                      </LazyRoute>
                    }
                  />

                  {/* Public findings share — PLAT-500. Org-level synthesis
                    across interviews; same unguessable-token trust model. */}
                  <Route
                    path="/public/findings/:token"
                    element={
                      <LazyRoute>
                        <PublicFindingsViewPage />
                      </LazyRoute>
                    }
                  />

                  {/* Public stakeholder interview — no Firebase auth.
                    Stakeholders click the magic link in their email and
                    drive their own SPD interview through the public
                    REST surface on rune-service. */}
                  <Route
                    path="/stakeholder/:token"
                    element={
                      <LazyRoute>
                        <StakeholderInterviewPage />
                      </LazyRoute>
                    }
                  />

                  {/* Public Coordinate team check-in — no Firebase auth.
                    Team members click the magic link in their cadence
                    email and run a brief 5-min check-in chat. Spec
                    lines 759–815. */}
                  <Route
                    path="/checkin/:token"
                    element={
                      <LazyRoute>
                        <TeamCheckInPage />
                      </LazyRoute>
                    }
                  />

                  {/* Public Map a Role interview — no Firebase auth.
                    Recipients click the magic-link emailed at
                    start-batch-roles time and complete their interview
                    as a guest. Spec lines 252–275. */}
                  <Route
                    path="/role-interview/:token"
                    element={
                      <LazyRoute>
                        <RoleInvitePage />
                      </LazyRoute>
                    }
                  />

                  {/* Public single-task SPD interview — magic-link redeem
                    page for the Document → Process Discovery flow. The
                    recipient walks the SPD agent's 8-phase chain and
                    finalizes a Structured Process Document. No Task
                    account needed; session bearer in memory. */}
                  <Route
                    path="/task-interview/:token"
                    element={
                      <LazyRoute>
                        <TaskInterviewPage />
                      </LazyRoute>
                    }
                  />

                  {/* Public help/FAQ — accessible without signing in so
                    confused users and prospects can read it. */}
                  <Route
                    path="/help"
                    element={
                      <LazyRoute>
                        <HelpPage />
                      </LazyRoute>
                    }
                  />
                  <Route
                    path="/trial-expired"
                    element={
                      <LazyRoute>
                        <TrialExpired />
                      </LazyRoute>
                    }
                  />

                  {/* Public provisioning routes (require auth but not admin) */}
                  <Route
                    path="/claim/:token"
                    element={
                      <LazyRoute>
                        <ClaimOrganization />
                      </LazyRoute>
                    }
                  />
                  <Route
                    path="/claim-organization"
                    element={
                      <LazyRoute>
                        <ClaimOrganization />
                      </LazyRoute>
                    }
                  />
                  <Route
                    path="/invite/:token"
                    element={
                      <LazyRoute>
                        <AcceptInvitation />
                      </LazyRoute>
                    }
                  />
                  <Route
                    path="/accept-invitation"
                    element={
                      <LazyRoute>
                        <AcceptInvitation />
                      </LazyRoute>
                    }
                  />

                  <Route
                    path="/onboarding"
                    element={
                      <ProtectedRoute>
                        <OnboardingProvider>
                          <LazyRoute>
                            <OnboardingContainer />
                          </LazyRoute>
                        </OnboardingProvider>
                      </ProtectedRoute>
                    }
                  />

                  {/* N-level OrgUnit routes — primary */}
                  {/* Legacy bookmark redirects — /organization/<name> → /organization/unit/<id> */}
                  <Route
                    path="/organization/:name"
                    element={
                      <ProtectedRoute>
                        <LazyRoute>
                          <LegacyDepartmentRedirect />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/organization/:name/:role"
                    element={
                      <ProtectedRoute>
                        <LazyRoute>
                          <LegacyRoleRedirect />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/projects/:projectId/interview/:planId"
                    element={
                      <ProtectedRoute>
                        <LazyRoute>
                          <ProjectInterview />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />



                  <Route
                    path="/manager/approvals"
                    element={<Navigate to="/settings/people" replace />}
                  />
                  <Route
                    path="/manager/approvals/:approvalId"
                    element={
                      <ProtectedRoute>
                        <LazyRoute>
                          <InterviewReviewDetail />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  {/* `/review/:token` was the old capability-link entry point.
                    The token is gone — redirect any in-flight links straight
                    to the interviews list. If they arrived here from a stale
                    email, they'll still find the approval in Settings →
                    Approvals. */}
                  <Route
                    path="/review/:token"
                    element={<Navigate to="/interviews" replace />}
                  />

                  {/* Interview Routes */}
                  {/* Redirect old paths to new unified page */}
                  <Route
                    path="/my-interviews"
                    element={<Navigate to="/interviews" replace />}
                  />
                  <Route
                    path="/interviews2"
                    element={<Navigate to="/interviews" replace />}
                  />
                  <Route
                    path="/my-interviews/:interviewId"
                    element={
                      <ProtectedRoute>
                        <LazyRoute>
                          <EmployeeInterview />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-interviews/:interviewId/review"
                    element={
                      <ProtectedRoute>
                        <LazyRoute>
                          <EmployeeTaskReview />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />

                  {/* Redirect legacy /admin to the Settings People tab, which
                      absorbed the old Team tab's membership + roles UI. */}
                  <Route
                    path="/admin"
                    element={<Navigate to="/settings/people" replace />}
                  />

                  {/* Super Admin routes */}
                  <Route
                    path="/admin/organizations/:orgId/claim"
                    element={
                      <ProtectedRoute requireSuperAdmin>
                        <LazyRoute>
                          <SendOrganizationClaim />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/trials"
                    element={
                      <ProtectedRoute requireSuperAdmin>
                        <LazyRoute>
                          <TrialManagement />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/prompt-optimization"
                    element={
                      <ProtectedRoute requireSuperAdmin>
                        <LazyRoute>
                          <AdminPromptOptimization />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />

                  {/* Cutover 2026-07-20 — the redesign owns the root paths.
                    Every /new/* bookmark strips its prefix; bare /new lands
                    on the Interviews hub. */}
                  <Route
                    path="/new"
                    element={<Navigate to="/interviews" replace />}
                  />
                  <Route
                    path="/new/*"
                    element={
                      <RedirectFromOldPath oldPrefix="/new" newPrefix="" />
                    }
                  />
                  <Route
                    path="/discover"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <PrototypePage kind="discover" />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  {/* Interviews tab — the merged Document+Design hub
                    (PLAT-450): one hero, one template catalog, one active
                    list. The PLAT-344 Unified Interview Index moves to
                    /new/interviews/all as the hub's "view all" inventory. */}
                  <Route
                    path="/interviews"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <InterviewsPage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/interviews/all"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <InterviewIndexPage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  {/* Diagnose tab — company-centric per Diagnose Tab spec
                    Functional Spec (PLAT-309). The `/company/` and
                    `/run/` prefixes disambiguate the two distinct
                    entity types underneath the tab; without them
                    `:orgId` and `:runId` would collide on the same
                    path slot. */}
                  <Route
                    path="/research"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <DiagnosePage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  {/* PLAT-451 — companies as the named secondary view under
                    the runs-first landing. */}
                  <Route
                    path="/research/companies"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <DiagnoseCompaniesPage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/research/company/:orgId"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <CompanyDetailPage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/research/company/:orgId/:section"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <CompanyDetailPage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/research/run/:runId"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <ProspectRunPage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/research/run/:runId/:tab"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <ProspectRunPage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/research/run/:runId/opportunities/:projectNumber"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <ProspectRunPage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  {/* Merged template catalog + interview-template authoring
                    (PLAT-318, re-homed by PLAT-450). */}
                  <Route
                    path="/interviews/templates"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <TemplatesCatalogPage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/interviews/templates/create"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <InterviewTypeAuthorWizard />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/interviews/templates/:id/edit"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <InterviewTypeAuthorWizard />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/interviews/templates/:id/sandbox"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <InterviewTypeSandboxRun />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  {/* PLAT-331 — workflow-template authoring, re-homed under
                    /new/interviews/workflows by PLAT-450 (distinct from
                    /templates so the two author wizards keep their own
                    URL space; /new/design/templates/* redirects here). */}
                  <Route
                    path="/interviews/workflows"
                    element={
                        <Navigate to="/interviews/templates" replace />
                    }
                  />
                  <Route
                    path="/interviews/workflows/create"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <ProjectTemplateAuthorWizard />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/interviews/workflows/:id/edit"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <ProjectTemplateAuthorWizard />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/interviews/workflows/:id/sandbox"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <ProjectTemplateSandboxRun />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/interviews/role/start/batch"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <RoleBatchInvitePage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/interviews/role/batch/:batchId"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <RoleBatchProgressPage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/interviews/role/:interviewId/spd"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <RoleSpdPage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  {/* Interview lock-in round 11 (F-14) — the cross-participant
                    consolidation for the role behind this interview. Linked
                    from the review page's footer actions; a sibling of /spd
                    so both deliverables hang off the same interview. */}
                  <Route
                    path="/interviews/role/:interviewId/consolidation"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <RoleConsolidationPage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/interviews/:mode/start"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <TemplateStartPage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/interviews/:mode/:interviewId"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <ProjectSessionPage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/interviews/:mode/:interviewId/review"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <InterviewModeReviewPage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  {/* PLAT-351 follow-up — interstitial Context page between
                    wizard submit and live session. Owner attaches context
                    docs before /dispatch-invites fires the emails. Serves
                    every template mode under the merged hub. */}
                  <Route
                    path="/interviews/:mode/:interviewId/context"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <ProjectContextPage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  {/* PLAT-450 — the retired Document and Design tabs redirect
                    into the merged Interviews hub. design/templates/* maps
                    to the workflow-authoring URL space; everything else is
                    a straight prefix swap (route ranking puts the static
                    templates rule ahead of the design splat). */}
                  <Route
                    path="/design/templates/*"
                    element={
                        <RedirectFromOldPath
                          oldPrefix="/design/templates"
                          newPrefix="/interviews/workflows"
                        />
                    }
                  />
                  <Route
                    path="/document/*"
                    element={
                        <RedirectFromOldPath
                          oldPrefix="/document"
                          newPrefix="/interviews"
                        />
                    }
                  />
                  <Route
                    path="/design/*"
                    element={
                        <RedirectFromOldPath
                          oldPrefix="/design"
                          newPrefix="/interviews"
                        />
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <SettingsPage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings/:tab"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <SettingsPage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/org"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <OrgPage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/org/unit/:unitId"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <OrgUnitPage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/org/unit/:unitId/role/:roleSlug"
                    element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <OrgRolePage />
                          </LazyRoute>
                        </ProtectedRoute>
                    }
                  />
                  {/* Legacy `/new/*` redirects — preserve external bookmarks
                    and any hard-coded internal links during the 60-day
                    soft-keep window (Frontend IA Spec §7.2). The wildcard
                    catches `/new/<old>` and every sub-path; the helper
                    rebuilds the equivalent URL under the new prefix. */}
                  <Route
                    path="/prospect/*"
                    element={
                        <RedirectFromOldPath
                          oldPrefix="/prospect"
                          newPrefix="/research"
                          nestedPrefix="/run"
                        />
                    }
                  />
                  <Route
                    path="/interview/*"
                    element={
                        <RedirectFromOldPath
                          oldPrefix="/interview"
                          newPrefix="/interviews"
                        />
                    }
                  />
                  <Route
                    path="/spark/*"
                    element={
                        <RedirectFromOldPath
                          oldPrefix="/spark"
                          newPrefix="/interviews"
                        />
                    }
                  />

                  {/* Cutover 2026-07-20 — legacy counterpart paths bridge
                    to the redesign. Features without a redesigned
                    counterpart (admin, approvals, evolution, projects,
                    my-interviews, …) keep their routes above. */}
                  <Route
                    path="/dashboard"
                    element={<Navigate to="/interviews" replace />}
                  />
                  <Route
                    path="/dashboard/operations"
                    element={<Navigate to="/interviews" replace />}
                  />
                  <Route
                    path="/organization"
                    element={<Navigate to="/org" replace />}
                  />
                  <Route
                    path="/organization/unit/:unitId"
                    element={<LegacyOrgUnitRedirect />}
                  />
                  <Route
                    path="/organization/unit/:unitId/:role"
                    element={<LegacyOrgUnitRedirect />}
                  />
                  {/* 2026-07-21 — the Diagnose tab became Research. Old links
                      (bookmarks, sent emails, stored bell rows) redirect. */}
                  <Route
                    path="/diagnose"
                    element={<Navigate to="/research" replace />}
                  />
                  <Route
                    path="/diagnose/*"
                    element={
                      <RedirectFromOldPath oldPrefix="/diagnose" newPrefix="/research" />
                    }
                  />
                  <Route
                    path="/ignite"
                    element={<Navigate to="/research" replace />}
                  />
                  {/* Emails and stored bell notifications historically linked
                      /ignite/runs/:id(/pipeline) — match BEFORE the generic
                      :runId route or "runs" parses as the run id. */}
                  <Route
                    path="/ignite/runs/:runId/*"
                    element={<LegacyIgniteRunRedirect />}
                  />
                  <Route
                    path="/ignite/runs/:runId"
                    element={<LegacyIgniteRunRedirect />}
                  />
                  <Route
                    path="/ignite/:runId/*"
                    element={<LegacyIgniteRunRedirect />}
                  />
                  <Route
                    path="/ignite/:runId"
                    element={<LegacyIgniteRunRedirect />}
                  />
                  <Route
                    path="/rune"
                    element={<Navigate to="/discover" replace />}
                  />
                  <Route
                    path="*"
                    element={
                      <LazyRoute>
                        <NotFound />
                      </LazyRoute>
                    }
                  />
                </Routes>
                </RouteErrorBoundary>
              </BrowserRouter>
            </TooltipProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
